import type { AppVersionEvent } from "./app-version-coordinator";
import { breadcrumb, captureError } from "./observability";

const CAPTURED_EVENTS = new Set<AppVersionEvent>([
  "check_failed",
  "preload_error",
  "reload_blocked",
]);

function eventLevel(event: AppVersionEvent): "info" | "warning" | "error" {
  if (event === "check_failed" || event === "preload_error" || event === "reload_blocked") {
    return "error";
  }
  if (
    event === "version_mismatch" ||
    event === "reload_deferred_offline" ||
    event === "check_unreachable" ||
    event === "preload_error_during_reload"
  ) {
    return "warning";
  }
  return "info";
}

function eventOutcome(event: AppVersionEvent, data: Record<string, unknown>): string {
  if (event === "version_current") return "same_version";
  if (event === "version_mismatch") return "update_available";
  if (event === "reload_deferred_offline") return "deferred_offline";
  if (event === "reload_blocked") return "guarded_repeat";
  if (event === "manual_reload") return "manual_reload";
  if (event === "check_failed") return "version_check_failed";
  if (event === "recovery_confirmed") return "recovered_after_reload";
  if (event === "reload_attempted") {
    if (data.trigger === "bootstrap_mismatch") return "bootstrap_reload";
    if (data.trigger === "navigation_mismatch") return "navigation_hard_load";
    return "preload_reload";
  }
  return event;
}

function stringTag(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Sentry skips an object it has seen before, even one `beforeSend` dropped, so a held preload
 * error that an error boundary already reported would never reach Sentry when its reload stalls.
 * A rejection can be any object, not only an `Error`; primitives carry no such mark.
 */
function freshCopy(error: unknown): unknown {
  if (typeof error !== "object" || error === null) return error;
  const { name, message, stack, cause } = error as Partial<Error>;
  const copy = new Error(typeof message === "string" ? message : String(error), { cause });
  if (typeof name === "string") copy.name = name;
  if (typeof stack === "string") copy.stack = stack;
  return copy;
}

export function reportAppVersionEvent(
  event: AppVersionEvent,
  data: Record<string, unknown>,
  error?: unknown
): void {
  const observedData: Record<string, unknown> = {
    ...data,
    outcome: eventOutcome(event, data),
  };
  breadcrumb(`app_version.${event}`, observedData, {
    category: "app",
    level: eventLevel(event),
  });

  if (!CAPTURED_EVENTS.has(event)) {
    return;
  }

  captureError(freshCopy(error) ?? new Error(`Application version recovery: ${event}`), {
    feature: "app-version-recovery",
    operation: event,
    tags: {
      app_version_outcome: stringTag(observedData.outcome),
      app_version_trigger: stringTag(observedData.trigger),
      app_version_route_family: stringTag(observedData.routeFamily),
      app_version_client_release: stringTag(observedData.clientRelease),
      app_version_served_release: stringTag(
        observedData.servedRelease ?? observedData.targetRelease
      ),
    },
    fingerprint: ["app-version-recovery", event],
    extra: observedData,
  });
}
